import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Calendar as CalendarIcon, Clock, Loader2, CalendarCheck } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import Reveal from "@/components/Reveal";
import { useT } from "@/i18n/LanguageContext";
import useEventTracking from "@/hooks/useEventTracking";
import { PHONE } from "@/constants/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(d) {
  if (!d) return "";
  const day = String(d.getDate()).padStart(2, "0");
  const month = d.toLocaleString("en-GB", { month: "short" });
  const year = d.getFullYear();
  return `${day} ${month} ${year}`;
}
function toIsoDate(d) {
  if (!d) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

export default function Booking() {
  const { t } = useT();
  const trackEvent = useEventTracking();
  const [form, setForm] = useState({
    name: "", phone: "", location: "", vehicle: "", tyre_size: "",
    service: "", preferred_time: "", notes: "",
  });
  const [date, setDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.location.trim()) {
      toast.error(t.booking.errors.missing); return;
    }
    if (!form.service) { toast.error(t.booking.errors.service); return; }
    if (!date) { toast.error(t.booking.errors.date); return; }
    if (!form.preferred_time) { toast.error(t.booking.errors.time); return; }

    setLoading(true);
    try {
      await axios.post(`${API}/bookings`, {
        ...form,
        preferred_date: toIsoDate(date),
      });
      trackEvent("booking_submit", "booking-form", { service: form.service })();
      toast.success(t.booking.success);
      setForm({
        name: "", phone: "", location: "", vehicle: "", tyre_size: "",
        service: "", preferred_time: "", notes: "",
      });
      setDate(null);
      setDone(true);
      setTimeout(() => setDone(false), 8000);
    } catch (err) {
      console.error(err);
      toast.error(`${t.booking.errors.network} ${PHONE}.`);
    } finally {
      setLoading(false);
    }
  };

  const today = new Date(); today.setHours(0, 0, 0, 0);

  return (
    <section
      id="book"
      data-testid="booking-section"
      className="relative bg-white py-16 sm:py-24 lg:py-32 border-t border-black/5"
    >
      <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <Reveal direction="left" className="lg:col-span-5">
            <div className="overline text-[#1E63FF]">{t.booking.overline}</div>
            <h2 className="font-display font-semibold text-3xl sm:text-5xl lg:text-6xl tracking-tight text-[#0A0A0A] mt-4 leading-[1.02]">
              {t.booking.heading1}<br />
              <span className="text-[#1E63FF]">{t.booking.heading2}</span>
            </h2>
            <p className="mt-5 sm:mt-6 text-neutral-600 text-sm sm:text-base leading-relaxed max-w-md">
              {t.booking.intro}
            </p>

            <ul className="mt-8 space-y-3 text-[15px] text-[#0A0A0A]">
              {t.booking.bullets.map((b, i) => (
                <li key={i} className="flex items-center gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#1E63FF]" />
                  {b}
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal direction="right" delay={120} className="lg:col-span-7">
            <form
              data-testid="booking-form"
              noValidate
              onSubmit={onSubmit}
              className="bg-[#F5F5F7] border border-black/10 rounded-2xl p-5 sm:p-10 lg:p-12 shadow-[0_30px_80px_-40px_rgba(0,0,0,0.25)]"
            >
              <div className="flex items-center gap-2 mb-7">
                <CalendarCheck className="w-4 h-4 text-[#1E63FF]" />
                <span className="overline text-neutral-500">{t.booking.formTitle}</span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field label={t.booking.fields.name}>
                  <input data-testid="booking-input-name" required value={form.name}
                    onChange={update("name")} placeholder={t.booking.placeholders.name} className="bk-input" />
                </Field>
                <Field label={t.booking.fields.phone}>
                  <input data-testid="booking-input-phone" type="tel" required value={form.phone}
                    onChange={update("phone")} placeholder={t.booking.placeholders.phone} className="bk-input" />
                </Field>
                <Field label={t.booking.fields.location}>
                  <input data-testid="booking-input-location" required value={form.location}
                    onChange={update("location")} placeholder={t.booking.placeholders.location} className="bk-input" />
                </Field>
                <Field label={t.booking.fields.vehicle}>
                  <input data-testid="booking-input-vehicle" value={form.vehicle}
                    onChange={update("vehicle")} placeholder={t.booking.placeholders.vehicle} className="bk-input" />
                </Field>
                <Field label={t.booking.fields.tyreSize}>
                  <input data-testid="booking-input-tyre-size" value={form.tyre_size}
                    onChange={update("tyre_size")} placeholder={t.booking.placeholders.tyreSize} className="bk-input" />
                </Field>
                <Field label={t.booking.fields.service}>
                  <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
                    <SelectTrigger data-testid="booking-select-service" className="bk-input h-auto">
                      <SelectValue placeholder={t.booking.placeholders.service} />
                    </SelectTrigger>
                    <SelectContent>
                      {t.booking.services.map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>

                <Field label={t.booking.fields.preferredDate}>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        data-testid="booking-date-trigger"
                        className="bk-input text-left inline-flex items-center justify-between gap-2"
                      >
                        <span className={date ? "text-[#0A0A0A]" : "text-neutral-400"}>
                          {date ? formatDate(date) : t.booking.placeholders.date}
                        </span>
                        <CalendarIcon className="w-4 h-4 text-neutral-500" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent align="start" className="p-0 bg-white">
                      <Calendar
                        mode="single"
                        selected={date}
                        onSelect={setDate}
                        disabled={(d) => d < today}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </Field>

                <Field label={t.booking.fields.preferredTime}>
                  <Select value={form.preferred_time}
                    onValueChange={(v) => setForm({ ...form, preferred_time: v })}>
                    <SelectTrigger data-testid="booking-select-time" className="bk-input h-auto">
                      <SelectValue placeholder={t.booking.placeholders.time}>
                        {form.preferred_time ? (
                          <span className="inline-flex items-center gap-2">
                            <Clock className="w-3.5 h-3.5" /> {form.preferred_time}
                          </span>
                        ) : null}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {t.booking.timeSlots.map((tt) => (
                        <SelectItem key={tt} value={tt}>{tt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>

                <Field label={t.booking.fields.notes} full>
                  <textarea
                    data-testid="booking-input-notes"
                    rows={3}
                    value={form.notes}
                    onChange={update("notes")}
                    placeholder={t.booking.placeholders.notes}
                    className="bk-input resize-none"
                  />
                </Field>
              </div>

              <div className="mt-7 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
                <p className="text-xs text-neutral-500 max-w-xs">
                  {t.booking.disclaimer}
                </p>
                <button
                  data-testid="booking-submit-btn"
                  type="submit"
                  disabled={loading}
                  className="btn-blue h-13 px-7 py-3.5 rounded-full inline-flex items-center justify-center gap-2.5 text-sm font-semibold disabled:opacity-60"
                >
                  {loading ? (<><Loader2 className="w-4 h-4 animate-spin" />{t.booking.sending}</>) :
                    (<><CalendarCheck className="w-4 h-4" />{t.booking.submit}</>)}
                </button>
              </div>

              {done && (
                <div data-testid="booking-success"
                  className="mt-6 p-4 rounded-xl bg-[#1E63FF]/8 border border-[#1E63FF]/20 text-[#0A0A0A] text-sm">
                  {t.booking.success}
                </div>
              )}
            </form>
          </Reveal>
        </div>
      </div>

      <style>{`
        .bk-input {
          width: 100%;
          padding: 0.875rem 1rem;
          background: #FFFFFF;
          border: 1px solid #E5E5E5;
          border-radius: 10px;
          font-family: 'Manrope', sans-serif;
          font-size: 0.95rem;
          color: #0A0A0A;
          outline: none;
          transition: border-color 200ms ease, background 200ms ease;
        }
        .bk-input:focus { border-color: #0A0A0A; }
        .bk-input::placeholder { color: #9CA3AF; }
      `}</style>
    </section>
  );
}

function Field({ label, full, children }) {
  return (
    <label className={`flex flex-col gap-2 ${full ? "sm:col-span-2" : ""}`}>
      <span className="text-xs font-semibold tracking-wider-2 uppercase text-neutral-500">{label}</span>
      {children}
    </label>
  );
}
